import { yupResolver } from '@hookform/resolvers/yup'
import { useState, useRef, useEffect, useCallback } from 'react'
import React, { FC } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { useLocation } from 'react-router-dom'

import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import ListIcon from '@mui/icons-material/List'
import { Box, Button, Typography, IconButton, Menu, Paper, Popover, Chip } from '@mui/material'

import { DashboardAnalyticsFilter, FieldType } from 'services/dashboard-analytics/types'

import DatePicker from 'components/common/DatePicker'
import Select from 'components/common/Select'
import TextInput from 'components/common/TextInput'

import AutoComplete from './Autocomplete'
import { getOperatorsForType, filterFields, autoCompleteOperators } from './consts'
import { filterSchema } from './validation'

export interface DashboardFilterRequest {
  filters: DashboardAnalyticsFilter[]
}

interface DashboardFilterComponentProps {
  onApplyFilters: (filters: DashboardFilterRequest) => void
  onClearFilters: () => void
  filters: DashboardAnalyticsFilter[]
}

interface FormFilter extends DashboardAnalyticsFilter {
  value?: string
}

interface FormValues {
  filters: FormFilter[]
}

const DashboardFilter: FC<DashboardFilterComponentProps> = ({
  onClearFilters,
  onApplyFilters,
  filters,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [activeFiltersCount, setActiveFiltersCount] = useState<number>(0)
  const [listAnchorEl, setListAnchorEl] = useState<HTMLElement | null>(null)
  const [selectedFilterIndex, setSelectedFilterIndex] = useState<number | null>(null)
  const anchorRef = useRef<HTMLDivElement>(null)
  const location = useLocation()

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      filters: [],
    },
    resolver: yupResolver(filterSchema),
    mode: 'onSubmit',
  })

  const { fields, prepend, remove } = useFieldArray({
    control,
    name: 'filters',
  })

  useEffect(() => {
    reset({ filters: filters })
    setActiveFiltersCount(filters.length)
  }, [])

  const addFilter = () => {
    prepend({
      column: '',
      operator: '',
      valueType: 'String',
      value: '',
      values: [],
    })
  }

  const removeFilter = (index: number) => {
    remove(index)
    handleListClose()
    setTimeout(() => {
      handleSubmit(submitWithoutClose)()
    }, 0)
  }

  const addValueToFilter = (filterIndex: number) => {
    const currentValue = watch(`filters.${filterIndex}.value`)
    if (currentValue && currentValue.trim() !== '') {
      const currentValues = watch(`filters.${filterIndex}.values`) || []
      const currentFilter = watch(`filters.${filterIndex}`)
      const trimmedValue = currentValue.trim()
      if (currentFilter?.column === 'l_event_date') {
        setValue(`filters.${filterIndex}.values`, [trimmedValue])
      } else {
        if (!currentValues.includes(trimmedValue)) {
          setValue(`filters.${filterIndex}.values`, [...currentValues, trimmedValue])
          setValue(`filters.${filterIndex}.value`, '')
        } else {
          setValue(`filters.${filterIndex}.value`, '')
          return
        }
      }
      setValue(`filters.${filterIndex}.value`, '')
      handleSubmit(submitWithoutClose)()
    }
  }

  const removeValueFromFilter = (filterIndex: number, valueIndex: number) => {
    const currentValues = watch(`filters.${filterIndex}.values`) || []
    const newValues = currentValues.filter((_, index) => index !== valueIndex)
    setValue(`filters.${filterIndex}.values`, newValues)

    if (newValues.length === 0) {
      removeFilter(filterIndex)
    } else {
      setTimeout(() => {
        handleSubmit(submitWithoutClose)()
      }, 0)
    }
  }

  const handleListClick = (event: React.MouseEvent<HTMLElement>, filterIndex: number) => {
    event.stopPropagation()
    setListAnchorEl(event.currentTarget)
    setSelectedFilterIndex(filterIndex)
  }

  const handleListClose = () => {
    setListAnchorEl(null)
    setSelectedFilterIndex(null)
  }

  const handleClear = () => {
    reset({ filters: [] })
    onClearFilters()
    setActiveFiltersCount(0)
    const paginationLS = JSON.parse(localStorage.getItem('pagination') || '{}')
    localStorage.setItem(
      'pagination',
      JSON.stringify({
        ...paginationLS,
        [location.pathname]: { filters: [] },
      })
    )
  }

  const getFieldType = (column: string): 'String' | 'Number' | 'Boolean' | 'Datetime' => {
    const field = filterFields.find((f) => f.column === column)
    return field?.type || 'String'
  }

  const processFormData = (data: FormValues, shouldClose: boolean = false) => {
    const paginationLS = JSON.parse(localStorage.getItem('pagination') || '{}')
    localStorage.setItem(
      'pagination',
      JSON.stringify({
        ...paginationLS,
        [location.pathname]: { filters: data.filters },
      })
    )
    const cleanedData = {
      filters: data.filters.map((filter: any) => ({
        column: filter.column,
        operator: filter.operator,
        valueType: filter.valueType,
        values: filter.values.filter((value: string) => value && value.trim() !== ''),
      })),
    }
    onApplyFilters(cleanedData)
    setActiveFiltersCount(cleanedData.filters.length)

    if (shouldClose) {
      handleClose()
    }
  }

  const onSubmit = (data: any) => {
    processFormData(data, true)
  }

  const submitWithoutClose = (data: any) => {
    processFormData(data, false)
  }

  const handleToggle = () => {
    setIsOpen((prevOpen) => !prevOpen)
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  const getAvailableOperators = (
    currentFilterIndex: number,
    column: string,
    fieldType: FieldType
  ) => {
    const allOperators = getOperatorsForType(fieldType)

    if (!column) return allOperators
    const usedOperators = fields
      .map((_, index) => {
        if (index === currentFilterIndex) return null
        const filter = watch(`filters.${index}`)
        return filter?.column === column ? filter.operator : null
      })
      .filter(Boolean)

    return allOperators.filter((operator) => !usedOperators.includes(operator.value))
  }

  const isFieldAvailable = (fieldValue: string, currentFilterIndex: number): boolean => {
    if (!fieldValue) return true

    const fieldUsageCount = fields.reduce((count, _, index) => {
      if (index === currentFilterIndex) return count
      const filter = watch(`filters.${index}`)
      return filter?.column === fieldValue ? count + 1 : count
    }, 0)

    const fieldType = getFieldType(fieldValue)
    const allOperators = getOperatorsForType(
      fieldType as 'String' | 'Number' | 'Boolean' | 'Datetime'
    )

    return fieldUsageCount < allOperators.length
  }

  const getAvailableFields = (currentFilterIndex: number) => {
    return filterFields.filter((field) => isFieldAvailable(field.column, currentFilterIndex))
  }

  const handleFieldChange = useCallback(
    (filterIndex: number, column: string) => {
      const fieldType = getFieldType(column)
      setValue(`filters.${filterIndex}.column`, column)
      setValue(`filters.${filterIndex}.valueType`, fieldType)
      setValue(`filters.${filterIndex}.operator`, '')
      setValue(`filters.${filterIndex}.value`, '')
      setValue(`filters.${filterIndex}.values`, [])
    },
    [setValue]
  )

  const handleOperatorChange = useCallback(
    (filterIndex: number, operator: string) => {
      setValue(`filters.${filterIndex}.operator`, operator)
      setValue(`filters.${filterIndex}.value`, '')
      setValue(`filters.${filterIndex}.values`, [])
    },
    [setValue]
  )

  return (
    <Box sx={{ mb: '16px' }}>
      <Box
        ref={anchorRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          userSelect: 'none',
        }}
        onClick={handleToggle}
      >
        <Typography variant="body1" sx={{ mr: 1 }}>
          Filters:
        </Typography>
        <IconButton size="small">
          {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>

        <Box
          sx={{
            p: '4px 8px',
            backgroundColor: '#e3f2fd',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography variant="body2" color="primary">
            Active filters: {activeFiltersCount} filter {activeFiltersCount > 1 ? 's' : ''} applied
          </Typography>
        </Box>
      </Box>

      <Menu
        anchorEl={anchorRef.current}
        open={isOpen}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              mt: '8px',
              borderRadius: '4px',
              minWidth: '600px',
              maxWidth: '1000px',
            },
          },
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        sx={{
          '& .MuiList-root': {
            padding: 0,
          },
          '& .MuiMenu-list': {
            padding: 0,
          },
        }}
      >
        <Paper
          sx={{
            p: '16px',
            border: '1px solid rgba(0, 0, 0, 0.12)',
            borderRadius: '4px',
            backgroundColor: '#fafafa',
            gap: '16px',
            display: 'flex',
            flexDirection: 'column',
          }}
          component="form"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={addFilter} size="small">
              Add Filter
            </Button>
            <Button variant="outlined" disabled={fields.length === 0} size="small" type="submit">
              Apply Filters
            </Button>
            <Button
              variant="outlined"
              onClick={handleClear}
              disabled={fields.length === 0}
              size="small"
            >
              Clear All
            </Button>
          </Box>
          {fields.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No filters applied. Click Add Filter to start filtering.
            </Typography>
          )}

          {fields.map((field, filterIndex) => {
            const currentFilter = watch(`filters.${filterIndex}`)
            const fieldType = getFieldType(currentFilter?.column || '')
            const availableOperators = getAvailableOperators(
              filterIndex,
              currentFilter?.column || '',
              fieldType
            )
            const availableFields = getAvailableFields(filterIndex)
            const url = filterFields.find((f) => f.column === currentFilter?.column)?.url
            const isDate = filterFields.find((f) => f.column === currentFilter?.column)?.isDate

            return (
              <Box
                key={field.id}
                sx={{
                  p: '16px',
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  borderRadius: '4px',
                  backgroundColor: '#fff',
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                  <Select
                    sx={{ flex: 1, minWidth: '210px' }}
                    control={control}
                    name={`filters.${filterIndex}.column`}
                    label="Field"
                    options={availableFields.map((f) => ({ value: f.column, label: f.label }))}
                    errors={errors?.filters?.[filterIndex]?.column?.message}
                    size="small"
                    onChange={(e) => handleFieldChange(filterIndex, e.target.value)}
                  />
                  <Select
                    sx={{ flex: 1, minWidth: '150px' }}
                    control={control}
                    name={`filters.${filterIndex}.operator`}
                    label="Operator"
                    options={availableOperators}
                    errors={errors?.filters?.[filterIndex]?.operator?.message}
                    size="small"
                    disabled={!currentFilter?.column}
                    onChange={(e) => handleOperatorChange(filterIndex, e.target.value)}
                  />

                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', gap: '16px' }}>
                      {url && autoCompleteOperators.includes(currentFilter.operator) ? (
                        <AutoComplete
                          sx={{ flex: 2, minHeight: 'auto', width: '232px' }}
                          control={control}
                          name={`filters.${filterIndex}.value`}
                          disabled={!currentFilter?.column || !currentFilter?.operator}
                          size="small"
                          placeholder="Enter value"
                          label="Value"
                          error={!!errors?.filters?.[filterIndex]?.value?.message}
                          helperText={errors?.filters?.[filterIndex]?.value?.message}
                          column={currentFilter?.column || ''}
                          url={url || ''}
                        />
                      ) : isDate ? (
                        <DatePicker
                          sx={{
                            flex: 3,
                            minHeight: 'auto',
                            width: '232px',
                            '& .MuiInputBase-root': {
                              height: '40px',
                              fontSize: '14px',
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: '16px',
                              transform: 'translate(14px, 10px) scale(1)',
                              '&.Mui-focused': {
                                transform: 'translate(14px, -9px) scale(0.75)',
                              },
                              '&.MuiFormLabel-filled': {
                                transform: 'translate(14px, -9px) scale(0.75)',
                              },
                            },
                            '& .MuiInputLabel-shrink': {
                              transform: 'translate(14px, -9px) scale(0.75)',
                            },
                          }}
                          name={`filters.${filterIndex}.value`}
                          label="Value"
                          errors={errors}
                          control={control}
                          disabled={!currentFilter?.column || !currentFilter?.operator}
                        />
                      ) : (
                        <TextInput
                          sx={{ flex: 2, minHeight: 'auto', width: '232px' }}
                          control={control}
                          name={`filters.${filterIndex}.value`}
                          label="Value"
                          errors={errors?.filters?.[filterIndex]?.value?.message}
                          size="small"
                          placeholder="Enter value"
                          disabled={!currentFilter?.column || !currentFilter?.operator}
                          onKeyDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                          onFocus={(e) => e.stopPropagation()}
                        />
                      )}
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ height: '40px', minWidth: '80px' }}
                        onClick={() => addValueToFilter(filterIndex)}
                        disabled={
                          !currentFilter?.column ||
                          !currentFilter?.operator ||
                          !currentFilter?.value
                        }
                      >
                        Add
                      </Button>
                      <IconButton
                        size="small"
                        sx={{ height: '40px', width: '40px' }}
                        onClick={(e) => handleListClick(e, filterIndex)}
                        disabled={!currentFilter?.column || !currentFilter?.values?.length}
                        color={currentFilter?.values?.length ? 'primary' : 'default'}
                      >
                        <ListIcon
                          sx={{
                            color: currentFilter?.values?.length ? '#1976d2' : '#9e9e9e',
                          }}
                        />
                      </IconButton>
                    </Box>
                    {errors?.filters?.[filterIndex]?.values?.message && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{
                          fontSize: '0.75rem',
                          mt: '4px',
                          textAlign: 'center',
                          alignSelf: 'start',
                          paddingLeft: '16px',
                        }}
                      >
                        {errors?.filters?.[filterIndex]?.values?.message}
                      </Typography>
                    )}
                  </Box>

                  <IconButton
                    size="small"
                    sx={{ height: '40px', width: '40px' }}
                    onClick={() => removeFilter(filterIndex)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
            )
          })}
        </Paper>
      </Menu>

      <Popover
        open={Boolean(listAnchorEl)}
        anchorEl={listAnchorEl}
        onClose={handleListClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              mt: '8px',
              p: '16px',
              minWidth: '200px',
              maxWidth: '300px',
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Added Values:
          </Typography>
          {selectedFilterIndex !== null &&
            watch(`filters.${selectedFilterIndex}.values`)?.map((value: string, index: number) => (
              <Chip
                key={index}
                label={value}
                onDelete={() => {
                  removeValueFromFilter(selectedFilterIndex, index)
                }}
                size="small"
                sx={{ mb: '4px' }}
              />
            ))}
          {selectedFilterIndex !== null &&
            (!watch(`filters.${selectedFilterIndex}.values`) ||
              watch(`filters.${selectedFilterIndex}.values`).length === 0) && (
              <Typography variant="body2" color="text.secondary">
                No values added yet
              </Typography>
            )}
        </Box>
      </Popover>
    </Box>
  )
}

export default DashboardFilter
